import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import { BarChart3, LineChart as LineChartIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (v) => Number(v ?? 0).toLocaleString();
const signed = (v) => `${v > 0 ? "+" : v < 0 ? "-" : "±"}${fmt(Math.abs(v))}`;
const monthYear = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });

const Segmented = ({ options, value, onChange, testId }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid={testId}>
    {options.map((o) => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${value === o.value ? "bg-white text-purple-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
        data-testid={`${testId}-${o.value}`}
        data-active={value === o.value}
      >
        {o.icon && <o.icon className="h-3.5 w-3.5" />}{o.label}
      </button>
    ))}
  </div>
);

const ChartTooltip = ({ active, payload, updatesByKey }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const ups = updatesByKey[p.key] || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs max-w-[260px]">
      <div className="font-semibold text-slate-800">{p.label}</div>
      <div className="text-slate-600 mt-0.5">Traffic: <span className="font-semibold tabular-nums">{p.traffic == null ? "—" : fmt(p.traffic)}</span></div>
      {p.change != null && (
        <div className={p.change > 0 ? "text-emerald-600" : p.change < 0 ? "text-red-600" : "text-slate-400"}>
          {signed(p.change)} vs previous period
        </div>
      )}
      {ups.map((u) => (
        <div key={u.id} className="mt-1 text-amber-700 font-medium">Google: {u.name}</div>
      ))}
    </div>
  );
};

const useTrafficHistory = (projectId, granularity, refreshKey) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    api.get(`/seo-performance/${projectId}/traffic-history`, { params: { granularity } })
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ loading: false, data: null, error: formatApiError(e) }); });
    return () => { alive = false; };
  }, [projectId, granularity, refreshKey]);
  return state;
};

export default function TrafficGrowthPanel({ projectId, refreshKey }) {
  const [granularity, setGranularity] = useState("week");
  const [chartType, setChartType] = useState("bar");
  const [updateFilter, setUpdateFilter] = useState("all");
  const { loading, data, error } = useTrafficHistory(projectId, granularity, refreshKey);

  const points = data?.points || [];
  const updates = data?.google_updates || [];
  const selected = updates.find((u) => u.id === updateFilter);
  const marks = updateFilter === "all" ? updates.filter((u) => u.bucket_key) : selected ? [selected] : [];
  const updatesByKey = useMemo(() => {
    const m = {};
    updates.forEach((u) => { if (u.bucket_key) (m[u.bucket_key] = m[u.bucket_key] || []).push(u); });
    return m;
  }, [updates]);
  const tracked = points.filter((p) => p.traffic != null);
  const totalChange = tracked.length > 1 ? tracked[tracked.length - 1].traffic - tracked[0].traffic : null;
  const labelOf = (k) => points.find((p) => p.key === k)?.label || k;

  const Chart = chartType === "bar" ? BarChart : LineChart;
  const common = [
    <CartesianGrid key="g" strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />,
    <XAxis key="x" dataKey="key" tickFormatter={labelOf} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} />,
    <YAxis key="y" tickFormatter={fmt} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={56} />,
    <Tooltip key="t" content={<ChartTooltip updatesByKey={updatesByKey} />} cursor={{ fill: "rgba(147,51,234,0.06)" }} />,
  ];

  return (
    <div className="px-5 py-4 bg-slate-50/70 border-t border-slate-100" data-testid={`seo-growth-panel-${projectId}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">Traffic growth</div>
          {totalChange != null && (
            <div className={`text-xs font-medium ${totalChange >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`seo-growth-total-${projectId}`}>
              {signed(totalChange)} since {tracked[0].label}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented testId={`seo-growth-granularity-${projectId}`} value={granularity} onChange={setGranularity}
            options={[{ value: "week", label: "Weekly" }, { value: "month", label: "Monthly" }]} />
          <Segmented testId={`seo-growth-type-${projectId}`} value={chartType} onChange={setChartType}
            options={[{ value: "bar", label: "Bar", icon: BarChart3 }, { value: "line", label: "Line", icon: LineChartIcon }]} />
          <Select value={updateFilter} onValueChange={setUpdateFilter}>
            <SelectTrigger className="h-8 w-[230px] text-xs bg-white" data-testid={`seo-growth-update-filter-${projectId}`}>
              <SelectValue placeholder="Google updates" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="seo-growth-update-option-all">All Google updates</SelectItem>
              <SelectItem value="none" data-testid="seo-growth-update-option-none">Hide Google updates</SelectItem>
              {updates.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`seo-growth-update-option-${u.id}`}>
                  {u.name} · {monthYear(u.begin)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="h-[240px] rounded-lg bg-slate-100 animate-pulse" />}
      {!loading && error && <div className="text-xs text-red-600 py-6 text-center" data-testid={`seo-growth-error-${projectId}`}>{error}</div>}
      {!loading && !error && points.length === 0 && (
        <div className="h-[140px] flex items-center justify-center text-xs text-slate-400 italic text-center px-6" data-testid={`seo-growth-empty-${projectId}`}>
          No traffic history yet. History is recorded from now on, every time Traffic is updated.
        </div>
      )}
      {!loading && !error && points.length > 0 && (
        <div className="h-[240px]" data-testid={`seo-growth-chart-${projectId}`} data-chart-type={chartType}>
          <ResponsiveContainer width="100%" height="100%">
            <Chart data={points} margin={{ top: 18, right: 16, left: 0, bottom: 0 }}>
              {common}
              {chartType === "bar"
                ? <Bar dataKey="traffic" fill="#9333ea" radius={[4, 4, 0, 0]} maxBarSize={48} />
                : <Line type="monotone" dataKey="traffic" stroke="#9333ea" strokeWidth={2.5} dot={{ r: 3, fill: "#9333ea" }} connectNulls />}
              {marks.filter((u) => u.bucket_key).map((u) => (
                <ReferenceLine key={u.id} x={u.bucket_key} stroke={selected ? "#d97706" : "#f59e0b"} strokeWidth={selected ? 2 : 1.5}
                  strokeDasharray={selected ? undefined : "4 3"}
                  label={selected ? { value: `${u.name} starts`, position: "insideTopLeft", fill: "#b45309", fontSize: 10 } : undefined} />
              ))}
              {selected?.end_bucket_key && selected.end_bucket_key !== selected.bucket_key && (
                <ReferenceLine x={selected.end_bucket_key} stroke="#d97706" strokeDasharray="4 3"
                  label={{ value: "rollout complete", position: "insideTopRight", fill: "#b45309", fontSize: 10 }} />
              )}
            </Chart>
          </ResponsiveContainer>
        </div>
      )}

      {!loading && !error && points.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5" data-testid={`seo-growth-update-note-${projectId}`}>
          <span className="inline-block h-3 w-0.5 bg-amber-500" />
          {updateFilter === "none" && "Google update markers hidden"}
          {updateFilter === "all" && `${marks.length} Google update${marks.length === 1 ? "" : "s"} in this range`}
          {selected && (selected.bucket_key
            ? `${selected.name} started ${new Date(selected.begin).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}`
            : `${selected.name} (${monthYear(selected.begin)}) happened outside the tracked range, so no marker is shown`)}
        </div>
      )}
    </div>
  );
}
